import React, { useContext, useState } from "react";
import {
  Card,
  CardContent,
  Container,
} from "@material-ui/core";
import PageTitle from "../../components/pageTitle/PageTitle";
import ThemeTable from "../../components/Table/ThemeTable";
import { Columns } from "./groupsTableColumns";
import DialogComponent from "../../components/DialogComponent/DialogComponent";
import DeleteGroup from "./Groups/DeleteGroup";
import EditGroup from "./Groups/EditGroup";
import { useEffect } from "react";
import { getGroupsList } from "../../services/apiservices/groups";
import AddNewGroup from "./Groups/AddNewGroup";
import { useMediaQuery } from "@mui/material";
import AuthContext from "../../hooks/authContext";
import {
  getCompanyEmployees,
} from "../../services/apiservices/employee";
import { Pencil, Trash, Plus } from "lucide-react";
import styles from "./employee.module.scss"
import { Button } from "../../components/ui/button";
import { trackEvent, CMS_EVENTS } from "../../services/apiservices/analytics";

const ManageGroups = (props) => {
  const [allGroups, setAllGroups] = React.useState([]);
  const [employeeList, setEmployeeList] = React.useState([]);
  const [searchGroupTerm, setSearchGroupTerm] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const isMobile = useMediaQuery("(max-width:767px)");
  const [isOpen, setIsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [data, setData] = useState();
  const isLandscape = useMediaQuery(
    "(min-width: 771px) and (max-width: 940px)"
  );
  const auth = useContext(AuthContext);
  const companyId =
    auth?.userProfile?.role === "companyUser"
      ? auth?.userProfile?.id
      : auth?.userProfile?.companyId;

  const fetchGroupsList = async () => {
    setIsLoading(true);
    // moveCompanyFiles("ckMwUiLZrTiVpJ6RIELG", "pRBTinHnTmcn6pPUsWaKRha5cYm1");
    try {
      const [employeeRes, res] = await Promise.all([
        new Promise((resolve, reject) => { getCompanyEmployees(companyId, (res) => resolve(res), (err) => resolve({ status: false, data: [] })); }),
        new Promise((resolve) => getGroupsList({}, resolve)),
      ]);

      const data = res?.filter((item) => item.companyId === companyId);
      const sortedEmployees = employeeRes?.data?.sort((a, b) => {
        const nameA = (a?.firstName + a?.lastName)?.toLowerCase();
        const nameB = (b?.firstName + b?.lastName)?.toLowerCase();
        return nameA?.localeCompare(nameB);
      });
      setEmployeeList(sortedEmployees);

      if (Array.isArray(data)) {
        const sortedGroups = data.sort((a, b) => {
          return new Date(b.createdAt) - new Date(a.createdAt);
        });

        const groupsWithMembers = sortedGroups?.map((group) => {
          const memberNames = group?.employeeId
            ?.map((empId) => {
              const employee = employeeRes?.data?.find(
                (emp) => emp.id === empId
              );
              return employee
                ? `${employee.firstName} ${employee.lastName}`
                : null;
            })
            ?.filter((name) => name !== null)
            ?.join(", ");

          return {
            ...group,
            members: memberNames,
          };
        });

        setAllGroups(groupsWithMembers);
      } else {
        console.error("Expected an array but got:", data);
      }
    } catch (resError) {
      console.error("Failed to fetch groups or employees", resError);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGroupsList();
    trackEvent(CMS_EVENTS.GROUP_VIEWED);
  }, []);

  const renderRowCell = (rcellItem, hcellItem, index, rowindex) => {
    switch (hcellItem.id) {
      case "title":
        return (
          <span
            style={{ fontSize: "14px", fontWeight: "500" }}
          >
            {rcellItem?.title}
          </span>
        );
      case "description":
        return (
          <span style={{ fontSize: "14px" }}>
            {rcellItem?.description}
          </span>
        );
      case "members":
        const membersArray = rcellItem.members
          ? rcellItem.members.split(", ")
          : [];

        const memberCount = membersArray.length;

        if (memberCount === 0) {
          return "No Members";
        }

        let displayNames = "";
        if (memberCount === 1) {
          displayNames = membersArray[0];
        } else if (memberCount === 2) {
          displayNames = `${membersArray[0]}, ${membersArray[1]}`;
        } else {
          const remainingCount = memberCount - 2;
          displayNames = `${membersArray[0]}, ${membersArray[1]
            }, +${remainingCount} member${remainingCount > 1 ? "s" : ""}`;
        }

        return (
          <div>
            <div
              style={{
                fontSize: "14px",
                lineHeight: "20px",
                fontWeight: "500",
              }}
            >
              {memberCount} member{memberCount > 1 ? "s" : ""}
            </div>
            <div style={{ color: "#6B7280", fontSize: "12px" }}>
              {displayNames}
            </div>
          </div>
        );
      case "type":
        return (
          <span style={{ fontSize: "14px" }}>
            {rcellItem?.type}
          </span>
        );
      case "action":
        return (
          <div className="d-flex flex-center flex-justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setData(rcellItem);
                setIsEditOpen(true);
              }}
              className="h-8 w-8 p-0 mr-10"
            >
              <Pencil className="h-4 w-4" />
            </Button>

            <DialogComponent
              buttonWrapClassName={"d-inline"}
              buttonClassName={"iconButton color-red"}
              buttonProps={{ size: "small" }}
              buttonLabel={<Trash className="h-4 w-4" />}
              ModalTitle={"Delete Group"}
              ModalContent={
                <DeleteGroup group={rcellItem} onSuccess={fetchGroupsList} />
              }
            />
          </div>
        );

      default:
        return rcellItem[hcellItem?.id];
    }
  };

  const filteredGroups =
    searchGroupTerm !== ""
      ? allGroups?.filter((group) => {
        return (
          String(group.title)
            .toLowerCase()
            .includes(String(searchGroupTerm).toLowerCase()) ||
          String(group.description)
            .toLowerCase()
            .includes(String(searchGroupTerm).toLowerCase())
        );
      })
      : allGroups;

  return (
    <>
      <Container>
        <PageTitle
          pageTitle="Team Groups"
          showBreadcrumb={[
            { label: "Home", to: "/" },
            { label: "Employee", to: "/peoples/manageEmp" },
          ]}
          showLastSeen={true}
          buttonTitle="Add Group"
          icons={<Plus className="h-4 w-4 mr-5" />}
          buttonClass={"d-flex flex-center"}
          buttonStyle={{
            color: "white",
            backgroundColor: "#16A34A",
            padding: "8px 16px",
            borderRadius: "5px",
          }}
          buttonClick={() => setIsOpen(true)}
        />
        <div className={isMobile ? "pv-20" : "p-24"}>
          <div style={{ margin: "20px auto" }}>
            <div
              className={
                isMobile
                  ? "d-flex flex-direction-column pv-15 ph-12"
                  : "d-flex flex-space-between flex-center mv-20"
              }
            >
              <div>
                <div
                  className="text-foreground"
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                  }}
                >
                  Group Management
                </div>
                <div
                  className="mv-10"
                  style={{ fontSize: "16px", color: "#6B7280" }}
                >
                  Manage and organize your team groups
                </div>
              </div>
            </div>
            <div
              className={`sectionBox dark:bg-[#1A1F2C] shadow-md dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]`}
              style={{
                // border: "1px solid hsl(214.3, 31.8%, 91.4%)",
                borderRadius: "8px",
                margin: isMobile ? "10px" : "",
              }}
            >
              <div className="pv-20">
                <div
                  className="text-foreground"
                  style={{
                    fontSize: "24px",
                    fontWeight: "500",
                  }}
                >
                  Available Groups
                </div>
                <div
                  style={{ color: "#64748B", fontSize: "14px" }}
                  className="mv-8"
                >
                  Manage your team groups and assign members to them.
                </div>
              </div>
              {!isMobile && !isLandscape && (
                <ThemeTable
                  rows={filteredGroups}
                  headCells={Columns}
                  hidePagination={true}
                  renderRowCell={renderRowCell}
                  isLoading={isLoading}
                />
              )}
              {(isMobile || isLandscape) &&
                filteredGroups?.map((record, index) => {
                  const membersArray = record.members
                    ? record.members.split(", ")
                    : [];

                  const memberCount = membersArray.length;

                  let displayNames = "";
                  if (memberCount === 1) {
                    displayNames = membersArray[0];
                  } else if (memberCount === 2) {
                    displayNames = `${membersArray[0]}, ${membersArray[1]}`;
                  } else {
                    const remainingCount = memberCount - 2;
                    displayNames = `${membersArray[0]}, ${membersArray[1]
                      }, +${remainingCount} member${remainingCount > 1 ? "s" : ""}`;
                  }
                  return (
                    <Card className={`${styles.card} dark:bg-[#2c3440]`}>
                      <CardContent style={{ padding: "0px 0px 16px 0px" }}>
                        <div className="d-flex flex-center flex-space-between">
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: "500",
                            }}
                            className="d-flex flex-direction-column text-foreground"
                          >
                            {record?.title}
                          </div>
                          <div className="d-flex flex-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setData(record);
                                setIsEditOpen(true);
                              }}
                              className="h-8 w-8 p-0"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <DialogComponent
                              buttonWrapClassName={"d-inline"}
                              buttonClassName={"iconButton color-danger"}
                              buttonProps={{ size: "small" }}
                              buttonLabel={<Trash className="h-4 w-4" />}
                              ModalTitle={"Delete Group"}
                              ModalContent={
                                <DeleteGroup
                                  group={record}
                                  onSuccess={fetchGroupsList}
                                />
                              }
                            />
                          </div>
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            lineHeight: "20px",
                            fontWeight: "500",
                          }}
                          className="mt-20 text-foreground"
                        >
                          {memberCount} member{memberCount > 1 ? "s" : ""}
                        </div>
                        <div style={{ color: "#6B7280", fontSize: "12px" }}>
                          {displayNames}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
            </div>
          </div>
        </div>
      </Container>

      {isOpen && (
        <AddNewGroup
          onAddSuccess={fetchGroupsList}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          onSuccess={fetchGroupsList}
          employeeList={employeeList}
        />
      )}
      {isEditOpen && (
        <EditGroup
          isEditOpen={isEditOpen}
          setIsEditOpen={setIsEditOpen}
          group={data}
          onSuccess={fetchGroupsList}
          employeeList={employeeList}
        />
      )}
    </>
  );
};
export default ManageGroups;
